import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import RecoCard from '../../components/recommendations/RecoCard';
import useFavorites from '../../utils/hook/useFavorites';
import { useUser } from '../../utils/hook/UserContext';
import useRecommendations from '../../utils/hook/useRecommendations';
import useSelection from '../../utils/hook/useSelection';

export default function RecommendationsModal({
  onClose,
  onOpenModal,
  onToggleSelect,
  onToggleFavorite,
  start,
  end,
  lat,
  lng,
}) {
  const { user } = useUser();
  const { items, source, status, reload } = useRecommendations({ start, end, lat, lng });
  const { favoriteIds, toggleFavorite } = useFavorites();
  const { selectedIds, toggleSelect } = useSelection();

  // Fermer avec Echap + bloquer le scroll de la page derrière
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="modal d-block reco-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onClick={onClose} // clic sur le fond = fermeture
      >
        <div
          className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered reco-modal-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-1">
                  <i className="bi bi-stars me-2" />
                  Recommandé pour vous
                </h5>
                <small className="text-muted">
                  Basé sur vos préférences, votre historique et la disponibilité.
                </small>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <Link
                  to="/preferences"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onClose}
                >
                  <i className="bi bi-sliders" /> Préférences
                </Link>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={reload}
                  disabled={status === 'loading'}
                >
                  <i className="bi bi-arrow-clockwise" /> Actualiser
                </button>

                <button type="button" className="btn-close" aria-label="Fermer" onClick={onClose} />
              </div>
            </div>

            <div className="modal-body">
              {status === 'loading' && (
                <div className="d-grid gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="placeholder-glow">
                      <div className="placeholder col-12 rounded-4" style={{ height: 220 }} />
                    </div>
                  ))}
                </div>
              )}

              {status === 'error' && (
                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                  Impossible de charger vos recommandations.
                  <button className="btn btn-sm btn-danger" onClick={reload}>
                    Réessayer
                  </button>
                </div>
              )}

              {status === 'ready' && items.length === 0 && (
                <div className="text-center py-5">
                  <p className="mb-3">Aucune annonce disponible pour le moment.</p>
                  <Link to="/search" className="btn btn-primary" onClick={onClose}>
                    Explorer toutes les annonces
                  </Link>
                </div>
              )}

              {status === 'ready' &&
                items.map((annonce) => (
                  <RecoCard
                    key={annonce.vehiculeAnnonceId}
                    annonce={annonce}
                    source={source}
                    searchStart={start}
                    searchEnd={end}
                    currentUserId={user?.id}
                    onOpenModal={onOpenModal}
                    isSelected={selectedIds.has(annonce.vehiculeAnnonceId)}
                    isFavorite={favoriteIds.has(annonce.vehiculeAnnonceId)}
                    onToggleSelect={toggleSelect}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show reco-backdrop" />
    </>,
    document.body
  );
}
